/**
 * Settings → Connections → Credit Monitoring Credentials
 *
 * Masked credential display with secure step-up reveal only.
 * Plaintext secrets are never returned by list APIs.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ProviderSavedCredentialPanel from '../../components/import/ProviderSavedCredentialPanel';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { buildRefreshActionState } from '../../utils/providerRefreshAction';
import { executeProviderRefreshAction } from '../../hooks/useProviderRefreshImport';
import {
  Shield,
  Eye,
  EyeOff,
  Save,
  Pencil,
  Trash2,
  Play,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  Link as LinkIcon,
} from 'lucide-react';
import { 
  getProvider, 
  getProviderDisplayInfo, 
  renderProviderLogo,
  normalizeProviderKey 
} from '../../config/providerRegistry';
import {
  getSensitiveCredentialFieldsToClear,
  providerRequiresVerificationField,
} from '../../config/providerCapabilities';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Provider display config - now uses centralized registry
const getProviderConfig = (providerKey) => {
  const info = getProviderDisplayInfo(providerKey);
  return {
    name: info.name,
    description: info.description,
    color: 'emerald', // Default color for card styling
    brand_color: info.brand_color,
  };
};

const ConnectionsSettings = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [canRevealCredentials, setCanRevealCredentials] = useState(true);
  const [revealBlockedReason, setRevealBlockedReason] = useState(null);
  
  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    ssn_last4: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [importing, setImporting] = useState(null);

  // Fetch providers
  const fetchProviders = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/settings/connections/providers`,
        { headers: getAuthHeaders() }
      );
      setProviders(response.data.providers || []);
      setCanRevealCredentials(response.data.canRevealCredentials !== false);
      setRevealBlockedReason(response.data.revealBlockedReason || null);
    } catch (error) {
      console.error('Failed to fetch providers:', error);
      toast.error('Failed to load provider connections');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const openEditModal = (provider, isNew = false) => {
    setSelectedProvider(provider);
    setFormData({
      username: '',
      password: '',
      ssn_last4: '',
      ...getSensitiveCredentialFieldsToClear(provider?.key),
    });
    setEditModalOpen(true);
  };

  useEffect(() => {
    if (!selectedProvider?.key) return;
    setFormData((prev) => ({
      ...prev,
      ...getSensitiveCredentialFieldsToClear(selectedProvider.key),
    }));
  }, [selectedProvider?.key]);

  // Save credentials
  const handleSaveCredentials = async () => {
    if (!selectedProvider) return;
    const needsVerification = providerRequiresVerificationField(selectedProvider.key);
    
    setSaving(true);
    try {
      const payload = {
        username: formData.username || null,
        password: formData.password || null,
      };
      if (needsVerification) {
        payload.ssn_last4 = formData.ssn_last4 || null;
      }
      if (selectedProvider.is_saved) {
        // Update existing
        await axios.put(
          `${API}/credit-report/credentials/${selectedProvider.key}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Credentials updated successfully');
      } else {
        // Save new
        await axios.post(
          `${API}/credit-report/credentials`,
          {
            provider: selectedProvider.key,
            username: formData.username,
            password: formData.password,
            ...(needsVerification ? { ssn_last4: formData.ssn_last4 || null } : {}),
          },
          { headers: getAuthHeaders() }
        );
        toast.success('Credentials saved successfully');
      }
      
      setEditModalOpen(false);
      fetchProviders();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  // Delete credentials
  const handleDeleteCredentials = async () => {
    if (!selectedProvider) return;
    
    try {
      await axios.delete(
        `${API}/credit-report/credentials/${selectedProvider.key}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Credentials deleted');
      setDeleteDialogOpen(false);
      fetchProviders();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete credentials');
    }
  };

  // Test connection
  const handleTestConnection = async (provider) => {
    setTesting(provider.key);
    try {
      const response = await axios.post(
        `${API}/settings/connections/providers/${provider.key}/test`,
        {},
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Test error:', error);
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(null);
    }
  };

  // Start re-import using shared refresh availability logic
  const handleReimport = async (provider) => {
    setImporting(provider.key);
    try {
      const refreshRes = await axios.get(
        `${API}/credit-report/refresh-status/${provider.key}`,
        { headers: getAuthHeaders() },
      );
      const actionState = buildRefreshActionState(refreshRes.data, { hasCredentials: true });

      await executeProviderRefreshAction({
        provider: provider.key,
        actionState,
        hasCredentials: true,
        onImportStarted: () => {
          toast.success(`Import started for ${provider.name}`);
        },
        onNeedCredentials: () => {
          openEditModal(provider);
        },
      });
    } catch (error) {
      console.error('Reimport error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to start import');
    } finally {
      setImporting(null);
    }
  };

  // Format timestamp
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Provider card component
  const ProviderCard = ({ provider }) => {
    const config = getProviderConfig(provider.key);
    
    const isSaved = provider.is_saved;
    const isPlaceholder = provider.provider_status === 'placeholder';
    const providerCanReveal = provider.canRevealCredentials ?? canRevealCredentials;
    const providerRevealBlocked = provider.revealBlockedReason ?? revealBlockedReason;
    
    return (
      <div
        data-testid={`provider-card-${provider.key}`}
        className={`p-4 border rounded-xl ${
          isSaved
            ? 'border-emerald-200 bg-emerald-50/50'
            : 'border-stone-200 bg-white'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Use centralized SVG logo - horizontal rectangular container */}
            <div className="w-32 h-10 flex items-center justify-start shrink-0">
              {renderProviderLogo(provider.key, 'sm')}
            </div>
            <div>
              <p className="font-medium text-stone-900">{config.name}</p>
              <p className="text-sm text-stone-500">{config.description}</p>
            </div>
          </div>
          
          {/* Status badge */}
          {isSaved ? (
            <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full flex items-center gap-1">
              <CheckCircle size={12} />
              Saved
            </span>
          ) : isPlaceholder ? (
            <span className="text-xs px-2 py-1 bg-stone-100 text-stone-500 rounded-full">
              Coming Soon
            </span>
          ) : (
            <span className="text-xs px-2 py-1 bg-stone-100 text-stone-600 rounded-full">
              Not Connected
            </span>
          )}
        </div>

        {isSaved && (
          <div className="mb-4">
            <ProviderSavedCredentialPanel
              provider={provider.key}
              settingsProvider={provider}
              canReveal={providerCanReveal}
              revealBlockedReason={providerRevealBlocked}
              variant="light"
            />
            <div className="pt-2 mt-2 border-t border-emerald-200 space-y-1">
              <div className="flex items-center justify-between text-xs text-stone-400">
                <span>Last Updated:</span>
                <span>{formatDate(provider.updated_at)}</span>
              </div>
              {provider.last_import_at && (
                <div className="flex items-center justify-between text-xs text-stone-400">
                  <span>Last Import:</span>
                  <span className="flex items-center gap-1">
                    {provider.last_import_success ? (
                      <CheckCircle size={10} className="text-emerald-500" />
                    ) : (
                      <AlertCircle size={10} className="text-red-500" />
                    )}
                    {formatDate(provider.last_import_at)}
                  </span>
                </div>
              )}
              {provider.use_count > 0 && (
                <div className="flex items-center justify-between text-xs text-stone-400">
                  <span>Times Used:</span>
                  <span>{provider.use_count}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {isSaved ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEditModal(provider)}
                data-testid={`edit-btn-${provider.key}`}
              >
                <Pencil size={14} className="mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedProvider(provider);
                  setDeleteDialogOpen(true);
                }}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid={`delete-btn-${provider.key}`}
              >
                <Trash2 size={14} className="mr-1" />
                Delete
              </Button>
              {!isPlaceholder && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestConnection(provider)}
                    disabled={testing === provider.key}
                    data-testid={`test-btn-${provider.key}`}
                  >
                    {testing === provider.key ? (
                      <RefreshCw size={14} className="mr-1 animate-spin" />
                    ) : (
                      <Play size={14} className="mr-1" />
                    )}
                    Test Connection
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleReimport(provider)}
                    disabled={importing === provider.key}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`reimport-btn-${provider.key}`}
                  >
                    {importing === provider.key ? (
                      <RefreshCw size={14} className="mr-1 animate-spin" />
                    ) : (
                      <RefreshCw size={14} className="mr-1" />
                    )}
                    Import Now
                  </Button>
                </>
              )}
            </>
          ) : (
            <Button
              variant={isPlaceholder ? 'outline' : 'default'}
              size="sm"
              disabled={isPlaceholder}
              onClick={() => openEditModal(provider, true)}
              data-testid={`save-btn-${provider.key}`}
            >
              {isPlaceholder ? (
                <>
                  <Clock size={14} className="mr-1" />
                  Coming Soon
                </>
              ) : (
                <>
                  <Save size={14} className="mr-1" />
                  Save Credentials
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="connections-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="connections-settings">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
            <Shield size={20} />
            Credit Monitoring Credentials
          </h2>
          <p className="text-sm text-stone-500">
            Manage saved logins for credit monitoring services. Reveal requires FundElite password verification.
          </p>
        </div>
      </div>

      {/* Provider cards */}
      <div className="space-y-4">
        {providers.map((provider) => (
          <ProviderCard key={provider.key} provider={provider} />
        ))}
      </div>

      {/* Help text */}
      <div className="p-4 bg-stone-50 rounded-lg">
        <h3 className="font-medium text-stone-700 mb-2">How it works</h3>
        <ul className="text-sm text-stone-600 space-y-1">
          <li>• Save your credit monitoring login credentials securely</li>
          <li>• Credentials are encrypted at rest using AES-256</li>
          <li>• Use <strong>Test Connection</strong> to verify credentials work</li>
          <li>• Use <strong>Import Now</strong> to pull your latest credit report</li>
          <li>• When importing from the main page, check "Save for later" to auto-save here</li>
        </ul>
      </div>

      {/* Edit/Save Modal */}
      <Dialog
        open={editModalOpen}
        onOpenChange={(open) => {
          setEditModalOpen(open);
          if (!open) {
            setFormData({
              username: '',
              password: '',
              ssn_last4: '',
              ...getSensitiveCredentialFieldsToClear(selectedProvider?.key),
            });
          }
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="credentials-modal">
          <DialogHeader>
            <DialogTitle>
              {selectedProvider?.is_saved ? 'Edit' : 'Save'} {getProviderDisplayInfo(selectedProvider?.key)?.name || 'Provider'} Credentials
            </DialogTitle>
            <DialogDescription>
              {selectedProvider?.is_saved 
                ? 'Update your saved credentials. Leave fields blank to keep existing values.'
                : 'Enter your login credentials to save for future imports.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedProvider?.is_saved && selectedProvider?.username_masked && (
              <p className="text-xs text-stone-500">
                Current username: <span className="font-mono">{selectedProvider.username_masked}</span>
              </p>
            )}
            <div className="space-y-2">
              <Label htmlFor="username">Username / Email</Label>
              <Input
                id="username"
                type="email"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder={selectedProvider?.is_saved ? 'Leave blank to keep saved username' : 'your-email@example.com'}
                data-testid="credential-username"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={selectedProvider?.is_saved ? 'Leave blank to keep saved password' : 'Enter password'}
                  data-testid="credential-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            
            {providerRequiresVerificationField(selectedProvider?.key) && (
              <div className="space-y-2">
                <Label htmlFor="ssn_last4">
                  Last 4 of SSN or Security Answer
                  {!selectedProvider?.is_saved && (
                    <span className="text-red-500 text-xs ml-2">*</span>
                  )}
                </Label>
                <Input
                  id="ssn_last4"
                  type="text"
                  value={formData.ssn_last4}
                  onChange={(e) => setFormData({ ...formData, ssn_last4: e.target.value })}
                  placeholder={selectedProvider?.is_saved ? 'Leave blank to keep saved value' : 'Enter your last 4 SSN digits or security answer'}
                  data-testid="credential-ssn"
                  required={!selectedProvider?.is_saved}
                />
                <p className="text-xs text-stone-500">
                  Required for security verification during import
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveCredentials} 
              disabled={
                saving
                || (!selectedProvider?.is_saved && (
                  !formData.username
                  || !formData.password
                  || (providerRequiresVerificationField(selectedProvider?.key) && !formData.ssn_last4)
                ))
              }
              data-testid="save-credentials-btn"
            >
              {saving ? (
                <RefreshCw size={14} className="mr-1 animate-spin" />
              ) : (
                <Save size={14} className="mr-1" />
              )}
              {selectedProvider?.is_saved ? 'Update' : 'Save'} Credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Saved Credentials?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your saved {getProviderDisplayInfo(selectedProvider?.key)?.name} credentials.
              You'll need to re-enter them for future imports.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCredentials}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ConnectionsSettings;
